import type { ReportFilterStateV1, SavedView } from "@meta-video/contracts";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { serializeSavedViewSearch } from "../../shared/url/workspaceSearch.js";
import { compactList, numericRangeSummary } from "./filterPresentation.js";
import { SavedViewActions } from "./SavedViewActions.js";
import { usePersistentFilterState } from "./FilterStateProvider.js";
import type { FilterStateController } from "./useFilterState.js";
import {
  SavedViewCollaborators,
  type SavedViewCollaboratorsApi,
} from "./SavedViewCollaborators.js";
import {
  SavedViewHeaderActions,
  type SavedViewsWriteApi,
} from "./SavedViews.js";

function accessLabel(role: SavedView["accessRole"]): string {
  if (role === "OWNER") return "Owner";
  if (role === "EDIT") return "Editor";
  return "Viewer";
}

function savedViewFirstSeenSummary(
  from: ReportFilterStateV1["firstSeenFrom"],
  to: ReportFilterStateV1["firstSeenTo"],
): string {
  if (from === null && to === null) return "any first-seen date";
  if (from === null) return `first seen through ${to}`;
  if (to === null) return `first seen from ${from}`;
  return `first seen ${from} to ${to}`;
}

export function savedViewFilterSummary(view: SavedView): string {
  const brands = compactList(view.labels.brands.map(({ name }) => name), "No brands");
  const accounts = view.filters.accountIds === null
    ? "all permitted accounts"
    : compactList(view.labels.accounts.map(({ name }) => name), "all permitted accounts");
  const date = view.filters.date.preset === "custom"
    ? `${view.filters.date.from} to ${view.filters.date.to}`
    : `last ${view.filters.date.preset} days`;
  const additions = [
    view.filters.adNameSearch ? `name: ${view.filters.adNameSearch}` : null,
    view.filters.objectives.length > 0
      ? `objectives: ${compactList(view.filters.objectives, "")}`
      : null,
    view.filters.groupSelections.length > 0
      ? `${view.filters.groupSelections.length} subgroup selection${view.filters.groupSelections.length === 1 ? "" : "s"}`
      : null,
    view.filters.durationMin !== null || view.filters.durationMax !== null
      ? `duration ${view.filters.durationMin ?? 0}–${view.filters.durationMax ?? "∞"} seconds`
      : "any duration",
    savedViewFirstSeenSummary(
      view.filters.firstSeenFrom,
      view.filters.firstSeenTo,
    ),
    `spend ${numericRangeSummary(view.filters.spendMin, view.filters.spendMax, "any")}`,
    `ROAS ${numericRangeSummary(view.filters.roasMin, view.filters.roasMax, "any")}`,
    view.filters.optimizationGoals.length > 0
      ? `optimization: ${compactList(view.filters.optimizationGoals, "")}`
      : null,
    view.filters.playStatuses.length > 0
      ? `playback: ${compactList(view.filters.playStatuses, "")}`
      : null,
  ].filter((value): value is string => value !== null);
  return [brands, accounts, date, ...additions].join(" · ");
}

function shortSavedViewUrl(viewId: string): string {
  return `/?${serializeSavedViewSearch(viewId)}`;
}

export interface SavedViewsPageContentProps {
  readonly collaboratorsApi?: SavedViewCollaboratorsApi;
  readonly onSessionExpired: () => void;
  readonly state: FilterStateController;
  readonly writeApi?: SavedViewsWriteApi;
}

export function SavedViewsPageContent({
  collaboratorsApi,
  onSessionExpired,
  state,
  writeApi,
}: SavedViewsPageContentProps) {
  const [collaboratorViewId, setCollaboratorViewId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const collaboratorTriggerRef = useRef<HTMLElement | null>(null);
  const actionTriggersRef = useRef(new Map<string, HTMLButtonElement>());
  const query = search.trim().toLocaleLowerCase("en-US");
  const views = useMemo(() => state.savedViews.filter((view) => {
    if (query === "") return true;
    return [
      view.name,
      view.ownerEmail,
      accessLabel(view.accessRole),
      savedViewFilterSummary(view),
    ].some((value) => value.toLocaleLowerCase("en-US").includes(query));
  }), [query, state.savedViews]);
  const currentFilters: ReportFilterStateV1 | null = state.status === "runnable"
    ? state.filters
    : null;
  const createAvailable = state.savedViewsStatus === "success"
    && currentFilters !== null
    && state.workspaceMutationKey !== null;
  const createUnavailableMessage = state.savedViewsStatus === "loading"
    ? "Wait for Saved Views to finish loading before creating a view."
    : state.savedViewsStatus === "error"
      ? "Retry loading Saved Views before creating a view."
      : "Open a valid Report before creating a Saved View.";
  const collaboratorView = collaboratorViewId === null
    ? null
    : state.savedViews.find((view) =>
      view.id === collaboratorViewId && view.accessRole === "OWNER") ?? null;

  useEffect(() => {
    if (
      collaboratorViewId !== null
      && collaboratorView === null
    ) setCollaboratorViewId(null);
  }, [collaboratorView, collaboratorViewId]);

  return (
    <section aria-labelledby="saved-views-page-heading" className="customer-route saved-views-page">
      <Stack gap="lg">
        <Group align="start" className="saved-views-page__heading" justify="space-between">
          <div>
            <Title id="saved-views-page-heading" order={2}>Saved Views</Title>
            <Text c="dimmed" size="sm">
              Open reusable report filters and collaborate with people who already have matching report access.
            </Text>
          </div>
          {createAvailable && currentFilters && state.workspaceMutationKey ? (
            <SavedViewHeaderActions
              activeView={null}
              api={writeApi}
              createLabel="New view"
              filters={currentFilters}
              isDirty={false}
              isPartial={false}
              onChanged={async (created, expectedWorkspaceKey) => {
                await state.adoptSavedViewIfCurrent(created, expectedWorkspaceKey);
                await state.refreshSavedViews();
              }}
              onMetadataChanged={state.refreshSavedViews}
              onSessionExpired={onSessionExpired}
              workspaceKey={state.workspaceMutationKey}
            />
          ) : (
            <Button
              aria-describedby="new-saved-view-unavailable"
              disabled
              size="xs"
              variant="default"
            >New view</Button>
          )}
        </Group>
        {!createAvailable ? (
          <Text c="dimmed" id="new-saved-view-unavailable" size="xs">
            {createUnavailableMessage}
          </Text>
        ) : null}

        {state.status === "blocked" ? (
          <Alert color="orange" title="Current Report context is unavailable">
            <Stack gap="xs">
              <Text size="sm">{state.message}</Text>
              <Button
                onClick={() => void (state.reason === "load-error"
                  ? state.retryLoads()
                  : state.resetFilters())}
                size="compact-sm"
                variant="light"
              >
                {state.reason === "load-error"
                  ? "Retry loading report context"
                  : "Reset Report filters"}
              </Button>
            </Stack>
          </Alert>
        ) : null}

        <TextInput
          aria-label="Search Saved Views"
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Search by name, owner, or filters"
          role="searchbox"
          ref={searchRef}
          value={search}
        />

        {state.notices.map((notice) => (
          <Alert color="orange" key={notice} title="Saved or shared view adjusted">
            {notice}
          </Alert>
        ))}

        {state.validationMessage ? (
          <Alert color="orange" role="alert" title="Saved View was not opened">
            {state.validationMessage}
          </Alert>
        ) : null}

        {state.savedViewsStatus === "loading" ? (
          <Group aria-live="polite" role="status">
            <Loader size="sm" />
            <Text>Loading Saved Views…</Text>
          </Group>
        ) : null}

        {state.savedViewsStatus === "error" ? (
          <Alert color="red" role="alert" title="Saved Views could not be loaded">
            <Stack gap="xs">
              <Text size="sm">Try again without changing your current report.</Text>
              <Button onClick={() => void state.refreshSavedViews()} size="compact-sm" variant="light">Retry</Button>
            </Stack>
          </Alert>
        ) : null}

        {state.savedViewsStatus === "success" && state.savedViews.length === 0 ? (
          <Text aria-live="polite" c="dimmed" role="status">
            No Saved Views are available yet. Save the current Report to create one.
          </Text>
        ) : null}

        {state.savedViewsStatus === "success" && state.savedViews.length > 0 && views.length === 0 ? (
          <Text aria-live="polite">No Saved Views match “{search.trim()}”.</Text>
        ) : null}

        {views.length > 0 ? (
          <div className="saved-view-list">
            <div aria-hidden="true" className="saved-view-list__header">
              <span>Name</span>
              <span>Summary</span>
              <span>Owner &amp; access</span>
              <span>Sharing</span>
              <span>Updated</span>
              <span>Actions</span>
            </div>
            <Stack component="ul" className="saved-view-list__rows" gap={0} m={0} p={0}>
              {views.map((view) => {
                const summary = savedViewFilterSummary(view);
                const isExpanded = expanded.has(view.id);
                const associated = state.activeView?.id === view.id;
                return (
                  <Paper
                    aria-label={`${view.name}, owned by ${view.ownerEmail}`}
                    className="saved-view-row"
                    component="li"
                    key={view.id}
                    p={0}
                    radius={0}
                  >
                    <div className="saved-view-row__grid">
                      <div
                        className="saved-view-row__identity"
                        data-saved-view-column="name"
                      >
                        <Title className="saved-view-row__name" order={3} title={view.name}>
                          {view.name}
                        </Title>
                        {view.isDefault || (associated && state.isDirty) ? (
                          <Group className="saved-view-row__metadata" gap="xs" mt={4} wrap="wrap">
                            {view.isDefault ? <Badge color="dark">Default</Badge> : null}
                            {associated && state.isDirty ? (
                              <Badge color="yellow">Unsaved changes</Badge>
                            ) : null}
                          </Group>
                        ) : null}
                      </div>
                      <div
                        className="saved-view-row__summary-cell"
                        data-saved-view-column="summary"
                      >
                        <Button
                          aria-controls={`saved-view-summary-${view.id}`}
                          aria-expanded={isExpanded}
                          className="saved-view-row__summary-trigger"
                          onClick={() => setExpanded((current) => {
                            const next = new Set(current);
                            if (next.has(view.id)) next.delete(view.id);
                            else next.add(view.id);
                            return next;
                          })}
                          size="compact-sm"
                          variant="subtle"
                        >{isExpanded ? "Hide filter summary" : "Show filter summary"}</Button>
                        <Text
                          className={isExpanded
                            ? "saved-view-row__summary saved-view-row__summary--expanded"
                            : "saved-view-row__summary"}
                          id={`saved-view-summary-${view.id}`}
                          size="sm"
                        >{summary}</Text>
                      </div>
                      <div
                        className="saved-view-row__access"
                        data-saved-view-column="access"
                      >
                        <Badge variant="light">{accessLabel(view.accessRole)}</Badge>
                        <Text c="dimmed" className="saved-view-row__owner" size="sm" title={view.ownerEmail}>
                          Owned by {view.ownerEmail}
                        </Text>
                      </div>
                      <div
                        className="saved-view-row__sharing"
                        data-saved-view-column="sharing"
                      >
                        <Badge color={view.isShared ? "blue" : "gray"} variant="light">
                          {view.isShared ? "Shared" : "Private"}
                        </Badge>
                        <Text c="dimmed" size="sm">
                          {view.peopleCount} {view.peopleCount === 1 ? "person" : "people"}
                        </Text>
                      </div>
                      <Text
                        c="dimmed"
                        className="saved-view-row__updated"
                        data-saved-view-column="updated"
                        size="xs"
                      >
                        Updated {new Date(view.updatedAt).toLocaleString()}
                      </Text>
                      <Group
                        className="saved-view-row__actions"
                        data-saved-view-column="actions"
                        gap="xs"
                        justify="flex-end"
                        wrap="wrap"
                      >
                        <SavedViewActions
                          api={writeApi}
                          currentFilters={currentFilters ?? undefined}
                          filterRevision={associated
                            ? state.activeView?.revision
                            : undefined}
                          includePrimaryActions
                          isAssociated={associated}
                          isDirty={associated && state.isDirty}
                          isOpening={state.openingSavedViewId === view.id}
                          isPartial={associated && state.isPartial}
                          onChanged={async (changed, expectedWorkspaceKey) => {
                            await state.adoptSavedViewIfCurrent(changed, expectedWorkspaceKey);
                            await state.refreshSavedViews();
                          }}
                          onDeleted={async (deletedViewId, expectedWorkspaceKey) => {
                            if (expectedWorkspaceKey !== null) {
                              await state.disassociateSavedViewIfCurrent(
                                deletedViewId,
                                expectedWorkspaceKey,
                              );
                            }
                            await state.refreshSavedViews();
                          }}
                          onManageCollaborators={() => {
                            collaboratorTriggerRef.current = actionTriggersRef.current.get(view.id)
                              ?? null;
                            setCollaboratorViewId(view.id);
                          }}
                          onMetadataChanged={async (changed) => {
                            if (changed) state.updateSavedViewBaseline(changed);
                            await state.refreshSavedViews();
                          }}
                          onOpen={() => state.openSavedView(view.id)}
                          onSessionExpired={onSessionExpired}
                          shortLinkUrl={shortSavedViewUrl(view.id)}
                          triggerRef={(node) => {
                            if (node) actionTriggersRef.current.set(view.id, node);
                            else actionTriggersRef.current.delete(view.id);
                          }}
                          view={view}
                          workspaceKey={state.workspaceMutationKey}
                        />
                      </Group>
                    </div>
                  </Paper>
                );
              })}
            </Stack>
          </div>
        ) : null}
      </Stack>

      <SavedViewCollaborators
        api={collaboratorsApi}
        fallbackFocusRef={searchRef}
        onClose={() => setCollaboratorViewId(null)}
        onReconcile={state.refreshSavedViews}
        onSessionExpired={onSessionExpired}
        opened={collaboratorView !== null}
        returnFocusRef={collaboratorTriggerRef}
        view={collaboratorView}
      />
    </section>
  );
}

export function SavedViewsPage({
  onSessionExpired,
}: {
  readonly onSessionExpired: () => void;
}) {
  const state = usePersistentFilterState();
  return <SavedViewsPageContent onSessionExpired={onSessionExpired} state={state} />;
}
